import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import BlogPost from '../components/BlogPost';
import { fetchBlogPostBySlug, fetchBlogPostsByNiche, BlogPost as BlogPostType } from '../utils/blogPosts';
import { NICHE_PILLAR_MAP } from '../utils/niches';

const BlogPostPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [post, setPost] = useState<BlogPostType | null>(null);
  const [loading, setLoading] = useState(true);
  const [siblingPosts, setSiblingPosts] = useState<BlogPostType[]>([]);

  useEffect(() => {
    const loadPost = async () => {
      if (id) {
        const foundPost = await fetchBlogPostBySlug(id);
        setPost(foundPost);
        if (foundPost?.niche) {
          const siblings = await fetchBlogPostsByNiche(foundPost.niche, {
            limit: 4,
            excludeSlug: foundPost.slug,
          });
          setSiblingPosts(siblings);
        }
      }
      setLoading(false);
    };
    loadPost();
  }, [id]);

  if (loading) {
    return (
      <main className="py-20">
        <div className="container-custom">
          <div className="flex items-center justify-center min-h-screen">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-600"></div>
          </div>
        </div>
      </main>
    );
  }

  if (!post) {
    return (
      <main className="py-20">
        <div className="container-custom">
          <div className="text-center">
            <h1 className="text-4xl font-heading font-bold text-gray-900 mb-4">
              Article Not Found
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              The article you're looking for doesn't exist or has been moved.
            </p>
            <a
              href="/blog"
              className="btn-primary"
            >
              Back to Blog
            </a>
          </div>
        </div>
      </main>
    );
  }

  // Article Schema for SEO
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "description": post.excerpt,
    "image": post.image || "https://www.sharonclare-hypnotherapy.com/images/Sharon Clare.png",
    "author": {
      "@type": "Person",
      "name": post.author,
      "url": "https://www.sharonclare-hypnotherapy.com/about/"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Sharon Clare Hypnotherapy",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.sharonclare-hypnotherapy.com/images/Sharon Clare Logo.png"
      }
    },
    "datePublished": post.publishedAt,
    "dateModified": post.publishedAt,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://www.sharonclare-hypnotherapy.com/blog/${post.id}`
    },
    "keywords": post.tags.join(', '),
    "articleSection": post.category
  };

  return (
    <>
      <Helmet>
        <title>{post.title} | Sharon Clare Hypnotherapy Blog</title>
        <meta name="description" content={post.excerpt} />
        <meta name="keywords" content={post.tags.join(', ')} />
        <meta name="author" content={post.author} />
        <link rel="canonical" href={`https://www.sharonclare-hypnotherapy.com/blog/${post.id}/`} />
        
        {/* Open Graph */}
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:url" content={`https://www.sharonclare-hypnotherapy.com/blog/${post.id}/`} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={post.image || "https://www.sharonclare-hypnotherapy.com/images/Sharon Clare.png"} />
        <meta property="article:published_time" content={post.publishedAt} />
        <meta property="article:author" content={post.author} />
        <meta property="article:section" content={post.category} />
        {post.tags.map(tag => (
          <meta key={tag} property="article:tag" content={tag} />
        ))}
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt} />
        <meta name="twitter:image" content={post.image || "https://www.sharonclare-hypnotherapy.com/images/Sharon Clare.png"} />
        
        {/* Article Schema */}
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      </Helmet>
      
      <main className="py-20">
        <div className="container-custom">
          <BlogPost
            id={post.slug}
            title={post.title}
            content={post.content}
            author={post.author}
            publishedAt={post.publishedAt}
            readTime={post.readTime}
            category={post.category}
            image={post.image}
            tags={post.tags}
            niche={post.niche}
            pillarSlug={post.pillarSlug}
            pillarLabel={post.niche ? NICHE_PILLAR_MAP[post.niche]?.label : undefined}
            siblingPosts={siblingPosts.map(s => ({ slug: s.slug, title: s.title, excerpt: s.excerpt }))}
          />
        </div>
      </main>
    </>
  );
};

export default BlogPostPage;
